import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Bot, Send, Upload, Image, MessageSquare, AlertTriangle, 
  Activity, FileText, Settings, Trash2, Plus, RefreshCw,
  CheckCircle, XCircle, Clock, Zap, Database, Server,
  Play, Square, AlertCircle, History, Shield
} from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCarCortex() {
  // Chat State
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // System Status State
  const [systemStatus, setSystemStatus] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  
  // Plans State
  const [plans, setPlans] = useState([]);
  const [planInput, setPlanInput] = useState('');
  const [planPriority, setPlanPriority] = useState('medium');
  
  // Config State
  const [config, setConfig] = useState({ model: 'gpt-4o' });
  
  // Action State
  const [auditLog, setAuditLog] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadConversations();
    loadSystemStatus();
    loadAlerts();
    loadNotifications();
    loadConfig();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  // === CONVERSATIONS ===
  const loadConversations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/carcortex/conversations`, getAuthHeaders());
      setConversations(res.data.conversations || []);
    } catch (err) {
      console.error('Error loading conversations:', err);
    }
  };

  const createNewConversation = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/carcortex/conversations`,
        { title: 'Neue Konversation' },
        getAuthHeaders()
      );
      await loadConversations();
      selectConversation(res.data.conversation_id);
    } catch (err) {
      console.error('Error creating conversation:', err);
    }
  };

  const selectConversation = async (conversationId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/carcortex/conversations/${conversationId}`,
        getAuthHeaders()
      );
      setActiveConversation(res.data);
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error('Error loading conversation:', err);
    }
  };

  const deleteConversation = async (conversationId) => {
    if (!window.confirm('Konversation wirklich löschen?')) return;
    try {
      await axios.delete(
        `${API_URL}/api/carcortex/conversations/${conversationId}`,
        getAuthHeaders()
      );
      if (activeConversation?.id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
      }
      loadConversations();
    } catch (err) {
      console.error('Error deleting conversation:', err);
    }
  };

  // === CHAT ===
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Prüfe Dateigröße (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Bild zu groß. Maximale Größe: 10MB');
        return;
      }
      // Prüfe Dateityp
      if (!file.type.startsWith('image/')) {
        alert('Bitte nur Bilddateien hochladen');
        return;
      }
      console.log('Selected image:', file.name, file.size, 'bytes', file.type);
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() && !selectedImage) return;
    
    setIsLoading(true);
    const userMessage = inputMessage;
    setInputMessage('');

    // Add user message to UI immediately
    const tempUserMsg = {
      id: 'temp-' + Date.now(),
      role: 'user',
      content: userMessage,
      has_image: !!selectedImage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      let response;
      
      if (selectedImage) {
        // Use form data for image upload
        const formData = new FormData();
        formData.append('message', userMessage || 'Analysiere dieses Bild');
        formData.append('image', selectedImage);
        if (activeConversation?.id) {
          formData.append('conversation_id', activeConversation.id);
        }

        console.log('Uploading image:', selectedImage.name, selectedImage.size, 'bytes');

        response = await axios.post(
          `${API_URL}/api/carcortex/chat/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            },
            timeout: 60000 // 60 Sekunden für Image-Upload
          }
        );
        console.log('Upload response:', response.data);
      } else {
        // Nutze intelligenten Chat mit Denkprozess
        response = await axios.post(
          `${API_URL}/api/carcortex/chat/intelligent`,
          {
            conversation_id: activeConversation?.id,
            message: userMessage
          },
          {
            ...getAuthHeaders(),
            timeout: 120000 // 2 Minuten für komplexe Analysen
          }
        );
      }

      if (response.data.success) {
        // Zeige Denkprozess + Antwort
        let fullResponse = response.data.response;
        
        // Füge Denkschritte hinzu wenn vorhanden
        if (response.data.thinking_steps) {
          fullResponse = `### 🧠 Analyse-Schritte:\n${response.data.thinking_steps}\n\n---\n\n${response.data.response}`;
        }
        
        const assistantMsg = {
          id: response.data.message_id || 'msg-' + Date.now(),
          role: 'assistant',
          content: fullResponse,
          thinking: response.data.thinking_steps,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev.filter(m => m.id !== tempUserMsg.id), tempUserMsg, assistantMsg]);

        // Update active conversation if new
        if (!activeConversation && response.data.conversation_id) {
          selectConversation(response.data.conversation_id);
          loadConversations();
        }
      }

      removeImage();
    } catch (err) {
      console.error('Error sending message:', err);
      // Add error message
      setMessages(prev => [...prev, {
        id: 'error-' + Date.now(),
        role: 'assistant',
        content: `❌ Fehler: ${err.response?.data?.detail || err.message}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // === SYSTEM STATUS ===
  const loadSystemStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/carcortex/system-status`, getAuthHeaders());
      setSystemStatus(res.data);
    } catch (err) {
      console.error('Error loading system status:', err);
    }
  };

  const runMonitoringCheck = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/carcortex/run-check`, {}, getAuthHeaders());
      await loadSystemStatus();
      await loadAlerts();
    } catch (err) {
      console.error('Error running check:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // === ACTIONS (Schreibrechte) ===
  const executeAction = async (actionName, parameters = {}, needsConfirm = false) => {
    if (needsConfirm && !window.confirm(`⚠️ Kritische Aktion: ${actionName}\n\nSind Sie sicher?`)) {
      return;
    }
    
    setActionLoading(actionName);
    try {
      const res = await axios.post(
        `${API_URL}/api/carcortex/actions/execute`,
        { action_name: actionName, parameters },
        getAuthHeaders()
      );
      
      if (res.data.requires_confirmation) {
        setPendingActions(prev => [...prev, {
          id: res.data.confirmation_id,
          action: actionName,
          message: res.data.message
        }]);
        alert(`⚠️ Diese Aktion erfordert Bestätigung.\nConfirmation ID: ${res.data.confirmation_id}`);
      } else if (res.data.success) {
        alert(`✅ ${res.data.message || 'Aktion erfolgreich'}`);
        // Refresh data
        loadSystemStatus();
        loadAlerts();
        loadAuditLog();
      } else {
        alert(`❌ ${res.data.error || 'Aktion fehlgeschlagen'}`);
      }
      
      return res.data;
    } catch (err) {
      console.error('Error executing action:', err);
      alert(`❌ Fehler: ${err.response?.data?.detail || err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const confirmPendingAction = async (confirmationId) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/carcortex/actions/confirm`,
        { confirmation_id: confirmationId },
        getAuthHeaders()
      );
      
      if (res.data.success) {
        alert(`✅ ${res.data.message || 'Aktion bestätigt und ausgeführt'}`);
        setPendingActions(prev => prev.filter(a => a.id !== confirmationId));
        loadSystemStatus();
        loadAlerts();
      }
    } catch (err) {
      console.error('Error confirming action:', err);
    }
  };

  const loadAuditLog = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/carcortex/actions/audit-log?limit=50`, getAuthHeaders());
      setAuditLog(res.data.audit_log || []);
    } catch (err) {
      console.error('Error loading audit log:', err);
    }
  };

  // Quick Actions
  const startJob = (jobType) => executeAction('start_job', { job_type: jobType });
  const stopCurrentJob = () => executeAction('stop_job', {});
  const stopAllJobs = () => executeAction('stop_all_jobs', {}, true);
  const resolveAllAlerts = () => executeAction('resolve_all_alerts', {});

  // === ALERTS ===
  const loadAlerts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/carcortex/alerts`, getAuthHeaders());
      setAlerts(res.data.alerts || []);
    } catch (err) {
      console.error('Error loading alerts:', err);
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      await axios.post(
        `${API_URL}/api/carcortex/alerts/resolve`,
        { alert_id: alertId },
        getAuthHeaders()
      );
      loadAlerts();
    } catch (err) {
      console.error('Error resolving alert:', err);
    }
  };

  // === NOTIFICATIONS ===
  const loadNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/carcortex/notifications`, getAuthHeaders());
      setNotifications(res.data.notifications || []);
    } catch (err) {
      console.error('Error loading notifications:', err);
    }
  };

  const dismissNotification = async (notificationId) => {
    try {
      await axios.post(
        `${API_URL}/api/carcortex/notifications/dismiss`,
        { notification_id: notificationId },
        getAuthHeaders()
      );
      loadNotifications();
    } catch (err) {
      console.error('Error dismissing notification:', err);
    }
  };

  // === PLANS ===
  const loadPlans = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/carcortex/plans`, getAuthHeaders());
      setPlans(res.data.plans || []);
    } catch (err) {
      console.error('Error loading plans:', err);
    }
  };

  const generatePlan = async () => {
    if (!planInput.trim()) return;
    setIsLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/carcortex/plans`,
        {
          feature_description: planInput,
          priority: planPriority,
          include_system_context: true
        },
        getAuthHeaders()
      );
      setPlanInput('');
      loadPlans();
      // Show plan in chat
      if (res.data.plan) {
        setMessages(prev => [...prev, {
          id: 'plan-' + Date.now(),
          role: 'assistant',
          content: res.data.plan,
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (err) {
      console.error('Error generating plan:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // === CONFIG ===
  const loadConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/carcortex/config`, getAuthHeaders());
      setConfig(res.data || { model: 'gpt-4o' });
    } catch (err) {
      console.error('Error loading config:', err);
    }
  };

  const updateConfig = async (newConfig) => {
    try {
      await axios.put(`${API_URL}/api/carcortex/config`, newConfig, getAuthHeaders());
      setConfig(newConfig);
    } catch (err) {
      console.error('Error updating config:', err);
    }
  };

  // === RENDER ===
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl">
            <Bot className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">CarCortex KI Agent</h1>
            <p className="text-gray-500">Intelligenter System-Assistent</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={systemStatus?.database?.status === 'healthy' ? 'default' : 'destructive'}>
            {systemStatus?.database?.status === 'healthy' ? '● Online' : '○ Offline'}
          </Badge>
          <Badge variant="outline">Model: {config.model}</Badge>
        </div>
      </div>

      {/* Notifications Banner */}
      {notifications.length > 0 && (
        <div className="mb-4 space-y-2">
          {notifications.slice(0, 3).map(notif => (
            <div 
              key={notif.id}
              className={`p-3 rounded-lg flex items-center justify-between ${
                notif.type === 'error' ? 'bg-red-50 border border-red-200' :
                notif.type === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
                'bg-blue-50 border border-blue-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className={`h-4 w-4 ${
                  notif.type === 'error' ? 'text-red-500' :
                  notif.type === 'warning' ? 'text-yellow-500' :
                  'text-blue-500'
                }`} />
                <span className="font-medium">{notif.title}</span>
                <span className="text-gray-600">{notif.message}</span>
              </div>
              <Button size="sm" variant="ghost" onClick={() => dismissNotification(notif.id)}>
                <XCircle className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="chat" className="space-y-4">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="chat" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" /> Chat
          </TabsTrigger>
          <TabsTrigger value="actions" className="flex items-center gap-2">
            <Zap className="h-4 w-4" /> Aktionen
          </TabsTrigger>
          <TabsTrigger value="monitoring" className="flex items-center gap-2">
            <Activity className="h-4 w-4" /> Monitoring
          </TabsTrigger>
          <TabsTrigger value="plans" className="flex items-center gap-2">
            <FileText className="h-4 w-4" /> Pläne
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" /> Einstellungen
          </TabsTrigger>
        </TabsList>

        {/* CHAT TAB */}
        <TabsContent value="chat">
          <div className="grid grid-cols-4 gap-4">
            {/* Conversations Sidebar */}
            <Card className="col-span-1">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Konversationen</CardTitle>
                  <Button size="sm" variant="ghost" onClick={createNewConversation}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2">
                    {conversations.map(conv => (
                      <div
                        key={conv.id}
                        className={`p-2 rounded-lg cursor-pointer flex items-center justify-between group ${
                          activeConversation?.id === conv.id 
                            ? 'bg-purple-100 border border-purple-300' 
                            : 'hover:bg-gray-100'
                        }`}
                        onClick={() => selectConversation(conv.id)}
                      >
                        <div className="truncate flex-1">
                          <p className="text-sm font-medium truncate">{conv.title}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(conv.updated_at).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="opacity-0 group-hover:opacity-100 hover:bg-red-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            deleteConversation(conv.id);
                          }}
                          title="Konversation löschen"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                    {conversations.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Keine Konversationen
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Chat Area */}
            <Card className="col-span-3">
              <CardContent className="p-4">
                {/* Messages */}
                <ScrollArea className="h-[450px] pr-4">
                  <div className="space-y-4">
                    {messages.length === 0 && (
                      <div className="text-center py-20">
                        <Bot className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">
                          Starte eine Konversation mit CarCortex
                        </p>
                        <p className="text-sm text-gray-400 mt-2">
                          Frage nach System-Status, analysiere Probleme oder erstelle Implementierungspläne
                        </p>
                      </div>
                    )}
                    {messages.map((msg, idx) => (
                      <div
                        key={msg.id || idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-3 ${
                            msg.role === 'user'
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {msg.has_image && (
                            <div className="mb-2">
                              <Badge variant="outline" className="text-xs">
                                <Image className="h-3 w-3 mr-1" /> Bild angehängt
                              </Badge>
                            </div>
                          )}
                          {msg.role === 'assistant' ? (
                            <div className="prose prose-sm max-w-none dark:prose-invert">
                              <ReactMarkdown>{msg.content}</ReactMarkdown>
                            </div>
                          ) : (
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          )}
                          <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-purple-200' : 'text-gray-400'}`}>
                            {new Date(msg.timestamp).toLocaleTimeString('de-DE')}
                          </p>
                        </div>
                      </div>
                    ))}
                    {isLoading && (
                      <div className="flex justify-start">
                        <div className="bg-gray-100 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <div className="animate-pulse flex space-x-1">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                            </div>
                            <span className="text-sm text-gray-500">CarCortex denkt...</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Image Preview */}
                {imagePreview && (
                  <div className="mt-2 relative inline-block">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="max-h-32 rounded-lg border"
                    />
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                      onClick={removeImage}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {/* Input Area */}
                <div className="mt-4 flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    title="Bild hochladen"
                  >
                    <Upload className="h-4 w-4" />
                  </Button>
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Nachricht an CarCortex..."
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    disabled={isLoading}
                    className="flex-1"
                  />
                  <Button 
                    onClick={sendMessage} 
                    disabled={isLoading || (!inputMessage.trim() && !selectedImage)}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ACTIONS TAB */}
        <TabsContent value="actions">
          <div className="grid grid-cols-2 gap-4">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" /> Schnellaktionen
                </CardTitle>
                <CardDescription>Direkte Systemsteuerung</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Job Control */}
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <Server className="h-4 w-4" /> Job-Steuerung
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => startJob('engine_collection')}
                      disabled={actionLoading === 'start_job'}
                    >
                      <Play className="h-4 w-4 mr-1" /> Engine Collection
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => startJob('full_update')}
                      disabled={actionLoading === 'start_job'}
                    >
                      <Play className="h-4 w-4 mr-1" /> Full Update
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={stopCurrentJob}
                      disabled={actionLoading === 'stop_job'}
                    >
                      <Square className="h-4 w-4 mr-1" /> Job Stoppen
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={stopAllJobs}
                      disabled={actionLoading === 'stop_all_jobs'}
                    >
                      <Square className="h-4 w-4 mr-1" /> Alle Stoppen
                    </Button>
                  </div>
                </div>

                {/* Alert Management */}
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" /> Alert-Management
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={resolveAllAlerts}
                      disabled={actionLoading === 'resolve_all_alerts'}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" /> Alle Alerts auflösen
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => executeAction('create_notification', {
                        title: 'Test Benachrichtigung',
                        message: 'Dies ist eine Testbenachrichtigung von CarCortex',
                        type: 'info'
                      })}
                    >
                      <MessageSquare className="h-4 w-4 mr-1" /> Test-Benachrichtigung
                    </Button>
                  </div>
                </div>

                {/* Data Collection */}
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <Database className="h-4 w-4" /> Datensammlung
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        const brand = prompt('Marke eingeben (z.B. BMW):');
                        if (brand) executeAction('trigger_data_collection', { brand, collect_type: 'all' });
                      }}
                    >
                      <RefreshCw className="h-4 w-4 mr-1" /> Marke sammeln
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pending Confirmations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" /> Ausstehende Bestätigungen
                </CardTitle>
                <CardDescription>Kritische Aktionen die Ihre Bestätigung benötigen</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingActions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                    <p>Keine ausstehenden Aktionen</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {pendingActions.map(action => (
                      <div key={action.id} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{action.action}</p>
                            <p className="text-sm text-gray-600">{action.message}</p>
                          </div>
                          <Button size="sm" onClick={() => confirmPendingAction(action.id)}>
                            Bestätigen
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Audit Log */}
            <Card className="col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" /> Audit-Log
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={loadAuditLog}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                <CardDescription>Alle ausgeführten CarCortex-Aktionen</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {auditLog.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>Keine Aktionen im Log</p>
                      <Button size="sm" variant="link" onClick={loadAuditLog}>
                        Log laden
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {auditLog.map((log, idx) => (
                        <div 
                          key={log.id || idx} 
                          className={`p-2 rounded text-sm ${
                            log.success ? 'bg-green-50' : 'bg-red-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {log.success ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                            <span className="font-medium">{log.action_name}</span>
                            <Badge variant="outline" className="text-xs">{log.action_type}</Badge>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(log.timestamp).toLocaleString('de-DE')}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* MONITORING TAB */}
        <TabsContent value="monitoring">
          <div className="grid grid-cols-3 gap-4">
            {/* System Overview */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5" /> Datenbank
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={loadSystemStatus}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {systemStatus?.database && (
                  <div className="space-y-2">
                    {Object.entries(systemStatus.database.collections || {}).map(([name, count]) => (
                      <div key={name} className="flex justify-between text-sm">
                        <span className="text-gray-600">{name}</span>
                        <span className="font-medium">{count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Jobs Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" /> Jobs
                </CardTitle>
              </CardHeader>
              <CardContent>
                {systemStatus?.jobs && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge variant={systemStatus.jobs.running > 0 ? 'default' : 'secondary'}>
                        {systemStatus.jobs.running} Laufend
                      </Badge>
                      {systemStatus.jobs.stuck_jobs?.length > 0 && (
                        <Badge variant="destructive">
                          {systemStatus.jobs.stuck_jobs.length} Hängend
                        </Badge>
                      )}
                    </div>
                    {systemStatus.jobs.running_jobs?.map(job => (
                      <div key={job.id} className="p-2 bg-green-50 rounded text-sm">
                        <p className="font-medium">{job.type}</p>
                        <p className="text-gray-600">
                          {job.manufacturers_processed}/{job.manufacturers_total}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Data Quality */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" /> Datenqualität
                </CardTitle>
              </CardHeader>
              <CardContent>
                {systemStatus?.data_quality && (
                  <div className="space-y-3">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-600">
                        {systemStatus.data_quality.completeness_score}%
                      </div>
                      <p className="text-sm text-gray-500">Vollständigkeit</p>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Fehlende Specs</span>
                        <span className="text-orange-500">{systemStatus.data_quality.missing_specs}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Fehlende Bilder</span>
                        <span className="text-orange-500">{systemStatus.data_quality.missing_images}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Fehlende Tuning</span>
                        <span className="text-orange-500">{systemStatus.data_quality.missing_tuning}</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Alerts */}
            <Card className="col-span-3">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" /> System-Alerts
                  </CardTitle>
                  <Button onClick={runMonitoringCheck} disabled={isLoading}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    Check ausführen
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                    <p>Keine aktiven Alerts</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {alerts.map(alert => (
                      <div 
                        key={alert.id}
                        className={`p-3 rounded-lg flex items-center justify-between ${
                          alert.severity === 'critical' ? 'bg-red-50 border border-red-200' :
                          alert.severity === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
                          'bg-blue-50 border border-blue-200'
                        }`}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant={
                              alert.severity === 'critical' ? 'destructive' :
                              alert.severity === 'warning' ? 'warning' : 'default'
                            }>
                              {alert.severity}
                            </Badge>
                            <span className="font-medium">{alert.title}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(alert.created_at).toLocaleString('de-DE')}
                          </p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => resolveAlert(alert.id)}>
                          <CheckCircle className="h-4 w-4 mr-1" /> Auflösen
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* PLANS TAB */}
        <TabsContent value="plans">
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Neuen Plan erstellen</CardTitle>
                <CardDescription>
                  Beschreibe ein Feature oder einen Fix und CarCortex erstellt einen Implementierungsplan
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  value={planInput}
                  onChange={(e) => setPlanInput(e.target.value)}
                  placeholder="Beschreibe das Feature oder den Fix..."
                  rows={5}
                />
                <div className="flex items-center gap-4">
                  <select
                    value={planPriority}
                    onChange={(e) => setPlanPriority(e.target.value)}
                    className="border rounded px-3 py-2"
                  >
                    <option value="low">Niedrig</option>
                    <option value="medium">Mittel</option>
                    <option value="high">Hoch</option>
                    <option value="critical">Kritisch</option>
                  </select>
                  <Button onClick={generatePlan} disabled={isLoading || !planInput.trim()}>
                    {isLoading ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4 mr-2" />
                    )}
                    Plan generieren
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Gespeicherte Pläne</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {plans.map(plan => (
                      <div key={plan.id} className="p-3 border rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={
                            plan.priority === 'critical' ? 'destructive' :
                            plan.priority === 'high' ? 'warning' : 'default'
                          }>
                            {plan.priority}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {new Date(plan.created_at).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                        <p className="text-sm">{plan.feature_description?.slice(0, 100)}...</p>
                      </div>
                    ))}
                    {plans.length === 0 && (
                      <p className="text-center text-gray-500 py-4">Keine Pläne vorhanden</p>
                    )}
                  </div>
                </ScrollArea>
                <Button variant="outline" className="w-full mt-2" onClick={loadPlans}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Aktualisieren
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* SETTINGS TAB */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>CarCortex Einstellungen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">KI-Modell</label>
                <select
                  value={config.model}
                  onChange={(e) => updateConfig({ ...config, model: e.target.value })}
                  className="border rounded px-3 py-2 w-full max-w-xs"
                >
                  <option value="gpt-4o">GPT-4o (Standard)</option>
                  <option value="gpt-4o-mini">GPT-4o Mini (Schneller)</option>
                  <option value="gpt-5">GPT-5 (Wenn verfügbar)</option>
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  Das Modell wird für alle CarCortex-Anfragen verwendet
                </p>
              </div>

              <div className="pt-4 border-t">
                <h3 className="font-medium mb-2">API-Konfiguration</h3>
                <p className="text-sm text-gray-500">
                  Der OpenAI API-Key wird aus den Admin-Einstellungen oder der Umgebungsvariable geladen.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
